import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { documentAPI } from "../services/api";
import { toast } from "react-toastify";
import {
  Calendar,
  Shield,
  User,
  ArrowLeft,
  AlertCircle,
  Copy,
  Download,
  ExternalLink,
} from "lucide-react";
import { safeFormatDateTime } from "../utils/dateUtils";

function DocumentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [docData, setDocData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        const response = await documentAPI.getDocument(id, true);
        setDocData(response.data);
      } catch (error) {
        toast.error("Failed to fetch document details");
        console.error("Document detail error:", error);
        navigate("/documents");
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [id, navigate]);

  const copySignature = () => {
    if (docData?.signature) {
      const copyToClipboard = async (text) => {
        try {
          if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(text);
            return true;
          } else {
            const textArea = document.createElement("textarea");
            textArea.value = text;
            textArea.style.position = "absolute";
            textArea.style.left = "-999999px";
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
              const successful = document.execCommand("copy");
              document.body.removeChild(textArea);
              return successful;
            } catch (err) {
              document.body.removeChild(textArea);
              return false;
            }
          }
        } catch (err) {
          return false;
        }
      };

      copyToClipboard(docData.signature).then((success) => {
        if (success) {
          toast.success("Signature copied to clipboard");
        } else {
          const message = `Copy this signature:\n\n${docData.signature}`;
          if (window.confirm(message + "\n\nClick OK to close this dialog.")) {
            // User clicked OK
          }
          toast.info("Please copy the signature manually");
        }
      });
    }
  };

  const downloadDocument = () => {
    if (docData?.documentData) {
      // Create download link for base64 data
      const link = document.createElement("a");
      link.href = docData.documentData;
      link.download = docData.documentName || "document";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Signed document downloaded");
    }
  };

  const shareForVerification = () => {
    const verificationUrl = `${window.location.origin}/verify/${id}`;

    // Try modern clipboard API first, fallback to older methods
    const copyToClipboard = async (text) => {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
          return true;
        } else {
          // Fallback for older browsers or non-secure contexts
          const textArea = document.createElement("textarea");
          textArea.value = text;
          textArea.style.position = "absolute";
          textArea.style.left = "-999999px";
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          try {
            const successful = document.execCommand("copy");
            document.body.removeChild(textArea);
            return successful;
          } catch (err) {
            document.body.removeChild(textArea);
            return false;
          }
        }
      } catch (err) {
        return false;
      }
    };

    copyToClipboard(verificationUrl).then((success) => {
      if (success) {
        toast.success("Public verification link copied to clipboard!");
      } else {
        // Show the URL in a modal/alert if copying fails
        const message = `Copy this verification link:\n\n${verificationUrl}`;
        if (window.confirm(message + "\n\nClick OK to close this dialog.")) {
          // User clicked OK
        }
        toast.info("Please copy the verification link manually");
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!docData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 mx-auto mb-4 text-red-500" />
          <h2 className="text-xl font-semibold text-gray-900">
            Document not found
          </h2>
          <button
            onClick={() => navigate("/documents")}
            className="mt-4 btn-primary"
          >
            Back to Documents
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/documents")}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Documents</span>
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {docData.documentName}
              </h1>
              <p className="mt-2 text-gray-600">
                {docData.description || "No description provided"}
              </p>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={downloadDocument}
                className="btn-secondary flex items-center space-x-1"
              >
                <Download className="h-4 w-4" />
                <span>Download Signed</span>
              </button>
              <button
                onClick={shareForVerification}
                className="btn-secondary flex items-center space-x-1 bg-green-600 hover:bg-green-700 text-white"
              >
                <ExternalLink className="h-4 w-4" />
                <span>Share for Verification</span>
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Document Info */}
            <div className="card p-6">
              <h2 className="text-lg font-semibold mb-4">
                Document Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    Document Name
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {docData.documentName}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    Document Type
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {docData.documentType || "PDF"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    File Size
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {docData.fileSize
                      ? (docData.fileSize / 1024).toFixed(1) + " KB"
                      : "Unknown"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    Category
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {docData.category || "Uncategorized"}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-500">
                    Description
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {docData.description || "No description"}
                  </p>
                </div>
              </div>

              {docData.tags && docData.tags.length > 0 && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-500 mb-2">
                    Tags
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {docData.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Digital Signature */}
            <div className="card p-6">
              <h2 className="text-lg font-semibold mb-4">Digital Signature</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">
                    Signature Hash
                  </label>
                  <div className="flex items-center space-x-2">
                    <code className="flex-1 p-3 bg-gray-100 rounded-md text-sm font-mono break-all">
                      {docData.signature}
                    </code>
                    <button
                      onClick={copySignature}
                      className="p-2 text-gray-400 hover:text-gray-600"
                      title="Copy signature"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    Document Hash
                  </label>
                  <code className="block mt-1 p-3 bg-gray-100 rounded-md text-sm font-mono break-all">
                    {docData.documentHash || "Not available"}
                  </code>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Signing Details */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold mb-4">Signing Details</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <User className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Signed by
                    </p>
                    <p className="text-sm text-gray-500">{docData.signedBy}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Calendar className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Signed on
                    </p>
                    <p className="text-sm text-gray-500">
                      {safeFormatDateTime(docData.signedAt)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Shield className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Security Level
                    </p>
                    <p className="text-sm text-gray-500">
                      Cryptographically Signed
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold mb-4">Actions</h3>
              <div className="space-y-2">
                <button
                  onClick={downloadDocument}
                  className="w-full btn-secondary"
                >
                  Download Document
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DocumentDetail;
